import { useMemo } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface FrictionPoint {
  id: string;
  location: string;
  hazard_category: string;
  friction_score: number;
  report_count: number;
  estimated_impact: 'high' | 'medium' | 'low' | null;
}

interface FrictionHeatMapProps {
  points: FrictionPoint[];
  onPointClick?: (point: FrictionPoint) => void;
}

export const FrictionHeatMap = ({ points, onPointClick }: FrictionHeatMapProps) => {
  // Group by location and aggregate
  const locationData = useMemo(() => {
    const grouped: Record<string, {
      totalScore: number;
      count: number;
      categories: Set<string>;
      maxImpact: 'high' | 'medium' | 'low' | null;
      points: FrictionPoint[];
    }> = {};

    points.forEach(point => {
      if (!grouped[point.location]) {
        grouped[point.location] = {
          totalScore: 0,
          count: 0,
          categories: new Set(),
          maxImpact: null,
          points: []
        };
      }

      const loc = grouped[point.location];
      loc.totalScore += point.friction_score;
      loc.count += point.report_count;
      loc.categories.add(point.hazard_category);
      loc.points.push(point);

      // Track highest impact
      if (point.estimated_impact === 'high') {
        loc.maxImpact = 'high';
      } else if (point.estimated_impact === 'medium' && loc.maxImpact !== 'high') {
        loc.maxImpact = 'medium';
      } else if (point.estimated_impact === 'low' && !loc.maxImpact) {
        loc.maxImpact = 'low';
      }
    });

    return Object.entries(grouped)
      .map(([location, data]) => ({
        location,
        avgScore: data.totalScore / data.points.length,
        totalReports: data.count,
        categoryCount: data.categories.size,
        maxImpact: data.maxImpact,
        points: data.points
      }))
      .sort((a, b) => b.avgScore - a.avgScore);
  }, [points]);

  // Find max score for normalization
  const maxScore = Math.max(...locationData.map(l => l.avgScore), 1);

  const getHeatColor = (score: number, impact: string | null) => {
    const intensity = score / maxScore;
    
    if (impact === 'high' || intensity > 0.7) {
      return `rgba(239, 68, 68, ${0.3 + intensity * 0.5})`; // red
    } else if (impact === 'medium' || intensity > 0.4) {
      return `rgba(245, 158, 11, ${0.3 + intensity * 0.5})`; // amber
    } else {
      return `rgba(34, 197, 94, ${0.2 + intensity * 0.4})`; // green
    }
  };

  if (points.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Tidak ada data friction point
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-400/50" />
            <span>Rendah</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-amber-400/60" />
            <span>Sedang</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-red-400/70" />
            <span>Tinggi</span>
          </div>
        </div>
        <span className="text-muted-foreground">
          {locationData.length} lokasi
        </span>
      </div>

      {/* Heat Map Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {locationData.map((loc, idx) => (
          <motion.button
            key={loc.location}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: idx * 0.03 }}
            onClick={() => loc.points[0] && onPointClick?.(loc.points[0])}
            className={cn(
              "relative p-4 rounded-xl border text-left transition-all hover:scale-105 hover:shadow-lg",
              "focus:outline-none focus:ring-2 focus:ring-primary/50"
            )}
            style={{ 
              backgroundColor: getHeatColor(loc.avgScore, loc.maxImpact),
              borderColor: loc.maxImpact === 'high' ? 'rgba(239, 68, 68, 0.5)' :
                          loc.maxImpact === 'medium' ? 'rgba(245, 158, 11, 0.5)' :
                          'rgba(34, 197, 94, 0.4)'
            }}
          >
            {/* Impact indicator */}
            {loc.maxImpact === 'high' && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            )}

            <div className="font-semibold text-sm mb-1 truncate">
              {loc.location}
            </div>
            
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {loc.totalReports} laporan
              </span>
              <span className={cn(
                "font-bold",
                loc.maxImpact === 'high' ? 'text-red-700' :
                loc.maxImpact === 'medium' ? 'text-amber-700' :
                'text-green-700'
              )}>
                {loc.avgScore.toFixed(1)}
              </span>
            </div>

            {/* Category dots */}
            <div className="flex gap-1 mt-2">
              {Array.from({ length: Math.min(loc.categoryCount, 5) }).map((_, i) => (
                <div 
                  key={i} 
                  className="w-1.5 h-1.5 rounded-full bg-foreground/30" 
                />
              ))}
              {loc.categoryCount > 5 && (
                <span className="text-xs text-muted-foreground">+{loc.categoryCount - 5}</span>
              )}
            </div>
          </motion.button>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 pt-4 border-t">
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">
            {locationData.filter(l => l.maxImpact === 'high').length}
          </div>
          <div className="text-xs text-muted-foreground">Lokasi Kritis</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold">
            {locationData.reduce((sum, l) => sum + l.totalReports, 0)}
          </div>
          <div className="text-xs text-muted-foreground">Total Laporan</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold">
            {(locationData.reduce((sum, l) => sum + l.avgScore, 0) / Math.max(locationData.length, 1)).toFixed(1)}
          </div>
          <div className="text-xs text-muted-foreground">Avg Friction Score</div>
        </div>
      </div>
    </div>
  );
};

export default FrictionHeatMap;
